import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity, ChevronDown, ChevronRight, Droplet, Minus, RefreshCw, Trash2,
  Stethoscope, TrendingDown, TrendingUp,
} from 'lucide-react';
import {
  Alert, Badge, Button, Card, Disclaimer, Empty, Field, Input, Loading, Page,
  PageHead, Stat, Tabs,
} from '../components/ui';
import Sparkline from '../components/Sparkline';
import { api } from '../lib/api';
import { useUndo } from '../lib/undo';
import {
  ALL_MARKERS, BLOOD_PANELS, MARKER_LABEL, n1, n2, riskBand, shortDate,
} from '../lib/format';

const DIRECTION = {
  improving: { tone: 'ok', color: 'var(--ok)', icon: TrendingDown, label: 'Improving' },
  worsening: { tone: 'crit', color: 'var(--crit)', icon: TrendingUp, label: 'Worsening' },
  stable: { tone: 'muted', color: 'var(--text-faint)', icon: Minus, label: 'Stable' },
};

const SPECIALIST_ICON = {
  Cardiologist: '🫀', Endocrinologist: '🧬', Hepatologist: '🫁', Nephrologist: '🫘',
  Hematologist: '🩸', Urologist: '🔬', Nutritionist: '🥗',
};

/* A single finding, laid out like a lab result line. */
function Finding({ a }) {
  const isTarget = a.basis === 'clinical_target';
  return (
    <div className="item">
      <span
        style={{
          width: 3, alignSelf: 'stretch', borderRadius: 2, flex: 'none',
          background: a.severity === 'critical' ? 'var(--crit)' : 'var(--warn)',
        }}
      />
      <div className="grow">
        <div className="item-title">{MARKER_LABEL[a.biomarker] || a.biomarker}</div>
        <div className="item-sub">
          {isTarget ? 'Outside the guideline target' : 'Outside the normal range'} · {a.range}
          {a.share_of_healthy_adults_also_outside != null && (
            <> · {n1(a.share_of_healthy_adults_also_outside)}% of healthy adults are too</>
          )}
        </div>
      </div>
      <div className="num" style={{ fontWeight: 600, fontSize: 14 }}>
        {a.value} <span className="faint" style={{ fontSize: 11 }}>{a.unit}</span>
      </div>
      <Badge tone={a.severity === 'critical' ? 'crit' : 'warn'}>
        {a.status === 'HIGH' ? 'High' : 'Low'}
      </Badge>
    </div>
  );
}

function Results({ result }) {
  const forest = result.isolation_forest || {};
  const detail = forest.detail || {};
  const band = riskBand(forest.risk_score ?? 0);
  const anomalies = result.anomalies || [];
  const specialists = result.specialist_recommendations || [];

  const real = anomalies.filter((a) => a.basis !== 'clinical_target');
  const targets = anomalies.filter((a) => a.basis === 'clinical_target');

  return (
    <div className="stack">
      <Card title="Risk score" sub="Reference checks combined with the multivariate detector">
        <div className="spread" style={{ alignItems: 'flex-end', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 40, fontWeight: 700, lineHeight: 1, color: band.color }} className="num">
              {forest.risk_score ?? '–'}
              <span style={{ fontSize: 15, color: 'var(--text-faint)', fontWeight: 600 }}> / 100</span>
            </div>
            <div style={{ marginTop: 8 }}><Badge tone={band.tone}>{band.label} risk</Badge></div>
          </div>
          <div style={{ textAlign: 'right', maxWidth: '48ch' }}>
            <div className="dim" style={{ fontSize: 12.5 }}>{forest.interpretation}</div>
          </div>
        </div>

        <div className="gauge">
          <span style={{ width: `${forest.risk_score ?? 0}%`, background: band.color }} />
        </div>

        {(detail.reference_component != null || detail.multivariate_percentile != null) && (
          <div className="grid grid-3" style={{ marginTop: 16 }}>
            <div>
              <div className="label">Reference checks</div>
              <div className="num" style={{ fontSize: 17, fontWeight: 600, marginTop: 3 }}>
                {n1(detail.reference_component)}
              </div>
              <div className="faint" style={{ fontSize: 11.5 }}>
                {detail.biomarkers_out_of_range} of {detail.biomarkers_provided} outside their bound
              </div>
            </div>
            <div>
              <div className="label">Multivariate pattern</div>
              <div className="num" style={{ fontSize: 17, fontWeight: 600, marginTop: 3 }}>
                {n1(detail.multivariate_percentile)}
              </div>
              <div className="faint" style={{ fontSize: 11.5 }}>percentile vs healthy adults</div>
            </div>
            <div>
              <div className="label">Critical values</div>
              <div className="num" style={{ fontSize: 17, fontWeight: 600, marginTop: 3 }}>
                {detail.critical_values ?? 0}
              </div>
              <div className="faint" style={{ fontSize: 11.5 }}>far outside their bound</div>
            </div>
          </div>
        )}

        {detail.explanation && (
          <p className="dim" style={{ fontSize: 12.5, marginTop: 14 }}>{detail.explanation}</p>
        )}

        {detail.driven_by?.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div className="label" style={{ marginBottom: 7 }}>Driven by</div>
            <div className="row">
              {detail.driven_by.map((d) => (
                <Badge key={d.biomarker} tone="accent">
                  {MARKER_LABEL[d.biomarker] || d.biomarker} · {n1(d.percentile_in_healthy)}th pct
                </Badge>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card
        title={`Findings (${anomalies.length})`}
        sub="A missed guideline target is shown separately from a true abnormality"
        flush
      >
        {anomalies.length === 0 ? (
          <Empty icon={Activity} title="Everything within range">
            No biomarker fell outside its reference interval or guideline target.
          </Empty>
        ) : (
          <>
            {real.length > 0 && (
              <>
                <div style={{ padding: '11px 16px 4px' }} className="label">Outside the normal range</div>
                {real.map((a) => <Finding key={a.biomarker} a={a} />)}
              </>
            )}
            {targets.length > 0 && (
              <>
                <div style={{ padding: '14px 16px 4px' }} className="label">
                  Outside a guideline target
                </div>
                {targets.map((a) => <Finding key={a.biomarker} a={a} />)}
              </>
            )}
          </>
        )}
      </Card>

      <Card title={`Suggested specialists (${specialists.length})`} flush>
        {specialists.length === 0 ? (
          <Empty icon={Stethoscope} title="No referral suggested">
            Nothing in this panel meets a referral threshold.
          </Empty>
        ) : (
          specialists.map((s) => (
            <div className="item" key={s.specialist}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>
                {SPECIALIST_ICON[s.specialist] || s.icon || '🩺'}
              </span>
              <div className="grow">
                <div className="item-title">{s.specialist}</div>
                <div className="item-sub">{s.reason}</div>
              </div>
              <Badge tone={s.urgency === 'high' ? 'crit' : s.urgency === 'moderate' ? 'warn' : 'muted'}>
                {s.urgency} urgency
              </Badge>
            </div>
          ))
        )}
        <div style={{ padding: '12px 16px 14px' }}>
          <Disclaimer>
            These suggestions are generated from your values by an automated rule set. They are
            decision support, not a diagnosis. Always consult a qualified healthcare professional.
          </Disclaimer>
        </div>
      </Card>
    </div>
  );
}

function TrendCard({ trend }) {
  const meta = DIRECTION[trend.direction] || DIRECTION.stable;
  const Icon = meta.icon;
  const change = trend.change > 0 ? `+${n2(trend.change)}` : n2(trend.change);

  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="spread" style={{ alignItems: 'flex-start', marginBottom: 8 }}>
        <div className="grow" style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {MARKER_LABEL[trend.biomarker] || trend.biomarker}
          </div>
          <div className="faint" style={{ fontSize: 11, marginTop: 1 }}>
            {trend.points.length} readings · {trend.unit}
          </div>
        </div>
        <Badge tone={meta.tone}><Icon size={11} /> {meta.label}</Badge>
      </div>

      <div className="row-tight" style={{ gap: 8, alignItems: 'baseline', marginBottom: 8 }}>
        <span className="num" style={{ fontSize: 21, fontWeight: 700 }}>
          {n1(trend.latest)}
        </span>
        <span className="num" style={{ fontSize: 12.5, color: meta.color, fontWeight: 600 }}>
          {change}
          {trend.percent_change != null && ` (${trend.percent_change > 0 ? '+' : ''}${trend.percent_change}%)`}
        </span>
      </div>

      <Sparkline
        points={trend.points}
        low={trend.reference_low}
        high={trend.reference_high}
        color={meta.color === 'var(--text-faint)' ? 'var(--accent)' : meta.color}
      />

      <div className="spread" style={{ marginTop: 8 }}>
        <span className="faint" style={{ fontSize: 11 }}>
          was {n1(trend.first)}
        </span>
        {trend.in_range_now ? (
          <span style={{ fontSize: 11, color: 'var(--ok)' }}>
            within {trend.basis === 'clinical_target' ? 'target' : 'range'}
          </span>
        ) : (
          <span style={{ fontSize: 11, color: trend.basis === 'clinical_target' ? 'var(--warn)' : 'var(--crit)' }}>
            {trend.basis === 'clinical_target' ? 'above target' : 'outside range'}
          </span>
        )}
      </div>
    </div>
  );
}

function Trends() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.bloodwork.trends()
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Card><Loading label="Building trends…" /></Card>;
  if (error) return <Alert tone="crit">{error}</Alert>;

  if (!data?.trends?.length) {
    return (
      <Card>
        <Empty icon={Activity} title="Not enough history yet">
          Trends need at least two panels containing the same biomarker. You have{' '}
          {data?.panels ?? 0}.
        </Empty>
      </Card>
    );
  }

  const counts = data.trends.reduce((acc, t) => {
    acc[t.direction] = (acc[t.direction] || 0) + 1;
    return acc;
  }, {});

  const shown = filter === 'all'
    ? data.trends
    : data.trends.filter((t) => t.direction === filter);

  return (
    <div className="stack">
      <div className="grid grid-4">
        <Stat label="Panels" value={data.panels}
          note={`${data.date_range.from} → ${data.date_range.to}`} />
        <Stat label="Improving" value={counts.improving || 0} tone="ok" />
        <Stat label="Worsening" value={counts.worsening || 0}
          tone={counts.worsening ? 'crit' : undefined} />
        <Stat label="Stable" value={counts.stable || 0} />
      </div>

      <div className="spread">
        <Tabs
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: `All ${data.tracked}` },
            { value: 'worsening', label: `Worsening ${counts.worsening || 0}` },
            { value: 'improving', label: `Improving ${counts.improving || 0}` },
            { value: 'stable', label: `Stable ${counts.stable || 0}` },
          ]}
        />
        <span className="faint" style={{ fontSize: 12 }}>
          Shaded band is your reference range
        </span>
      </div>

      {shown.length === 0 ? (
        <Card><Empty title={`Nothing ${filter}`}>
          Good news, in this case.
        </Empty></Card>
      ) : (
        <div className="grid grid-3">
          {shown.map((t) => <TrendCard key={t.biomarker} trend={t} />)}
        </div>
      )}

      <Disclaimer>
        A direction is only reported when the movement exceeds normal assay and
        biological variation; smaller shifts are shown as stable. Trends describe
        what your numbers did, not why — discuss anything concerning with a doctor.
      </Disclaimer>
    </div>
  );
}

export default function Bloodwork() {
  const [tab, setTab] = useState('enter');
  const [values, setValues] = useState({});
  const [open, setOpen] = useState({ cbc: true });
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState('');
  // How many people the detector was actually trained on. Read from the API
  // rather than typed here: this page makes a claim about the strength of the
  // evidence behind a health reading, and as a literal it had already drifted
  // from the deployed model.
  const [model, setModel] = useState(null);
  const { undoableDelete } = useUndo();

  const loadHistory = async () => {
    setLoadingHistory(true);
    try { setHistory(await api.bloodwork.list()); }
    catch (e) { setError(e.message); }
    finally { setLoadingHistory(false); }
  };

  useEffect(() => {
    loadHistory();
    api.bloodwork.modelInfo().then(setModel).catch(() => setModel(null));
  }, []);

  const filled = useMemo(
    () => ALL_MARKERS.filter((m) => values[m.key] !== undefined && values[m.key] !== '').length,
    [values]
  );

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      const payload = { test_date: new Date().toISOString().slice(0, 10) };
      ALL_MARKERS.forEach((m) => {
        const v = values[m.key];
        if (v !== undefined && v !== '') payload[m.key] = parseFloat(v);
      });
      const res = await api.bloodwork.submit(payload);
      setResult(res);
      setTab('results');
      loadHistory();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const reanalyse = async (id) => {
    setBusy(true);
    try { setResult(await api.bloodwork.analyse(id)); setTab('results'); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  // A panel is the most costly thing on this page to lose — it is a blood test,
  // not a meal — so the same undo window applies, and the analysis view is only
  // cleared once the deletion actually commits.
  const remove = (id) => {
    const index = history.findIndex((x) => x.id === id);
    if (index < 0) return;
    const panel = history[index];
    undoableDelete({
      label: 'Bloodwork panel',
      remove: () => setHistory((h) => h.filter((x) => x.id !== id)),
      restore: () => setHistory((h) => {
        if (h.some((x) => x.id === id)) return h;
        const next = [...h];
        next.splice(Math.min(index, next.length), 0, panel);
        return next;
      }),
      commit: (opts) => api.bloodwork.remove(id, opts),
      onCommitted: () => { if (result?.log?.id === id) setResult(null); },
      onError: (err) => setError(err.message),
    });
  };

  const panelFilled = (panel) =>
    panel.markers.filter((m) => values[m.key] !== undefined && values[m.key] !== '').length;

  return (
    <Page>
      <PageHead
        title="Bloodwork"
        actions={
          <Tabs
            value={tab}
            onChange={setTab}
            options={[
              { value: 'enter', label: 'Enter results' },
              { value: 'results', label: 'Analysis' },
              { value: 'trends', label: 'Trends' },
              { value: 'history', label: `History (${history.length})` },
            ]}
          />
        }
      >
        Enter any subset of {model?.biomarkers ?? 27} biomarkers. Values are checked against
        sex-specific reference intervals and guideline targets
        {model?.source === 'nhanes' && model.training_rows > 0
          ? `, then against a detector trained on ${model.training_rows.toLocaleString()} healthy NHANES adults.`
          : '.'}
      </PageHead>

      {error && <Alert tone="crit">{error}</Alert>}

      {tab === 'enter' && (
        <>
          <div className="spread">
            <div className="dim" style={{ fontSize: 13 }}>
              <strong className="num">{filled}</strong> of {ALL_MARKERS.length} entered — a partial
              panel is fine, missing values are inferred from what you provide.
            </div>
            <div className="row-tight">
              {filled > 0 && (
                <Button variant="quiet" size="sm" onClick={() => setValues({})}>Clear</Button>
              )}
              <Button variant="primary" onClick={submit} disabled={busy || filled === 0} loading={busy}>
                {busy ? 'Analysing…' : 'Analyse panel'}
              </Button>
            </div>
          </div>

          <div>
            {BLOOD_PANELS.map((panel) => {
              const isOpen = !!open[panel.id];
              const count = panelFilled(panel);
              return (
                <div className="panel" key={panel.id}>
                  <button
                    className="panel-head"
                    aria-expanded={isOpen}
                    onClick={() => setOpen((o) => ({ ...o, [panel.id]: !o[panel.id] }))}
                  >
                    {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    <span className="panel-dot" style={{ background: panel.accent }} />
                    <span className="grow">{panel.name}</span>
                    <Badge tone={count ? 'accent' : 'muted'}>
                      {count}/{panel.markers.length}
                    </Badge>
                  </button>
                  {isOpen && (
                    <div className="panel-body">
                      <div className="grid grid-3" style={{ marginTop: 14 }}>
                        {panel.markers.map((m) => (
                          <Field key={m.key} label={`${m.label} · ${m.unit}`} hint={`Ref ${m.ref}`}>
                            <Input
                              numeric
                              type="number"
                              step="any"
                              placeholder="–"
                              value={values[m.key] ?? ''}
                              onChange={(e) =>
                                setValues((v) => ({ ...v, [m.key]: e.target.value }))
                              }
                            />
                          </Field>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'results' && (
        result
          ? <Results result={result} />
          : <Card><Empty icon={Droplet} title="No analysis yet">
              Enter a panel and select Analyse, or re-run a past result from History.
            </Empty></Card>
      )}

      {tab === 'trends' && <Trends />}

      {tab === 'history' && (
        <Card title="Previous panels" flush>
          {loadingHistory ? <Loading /> : history.length === 0 ? (
            <Empty icon={Droplet} title="Nothing submitted yet">
              Panels you analyse are saved here so you can track changes over time.
            </Empty>
          ) : (
            history.map((log) => {
              const entered = ALL_MARKERS.filter((m) => log[m.key] != null).length;
              return (
                <div className="item" key={log.id}>
                  <div className="grow">
                    <div className="item-title">{log.test_date || shortDate(log.logged_at)}</div>
                    <div className="item-sub">
                      {entered} biomarkers · logged {shortDate(log.logged_at)}
                    </div>
                  </div>
                  <Button size="sm" icon={RefreshCw} onClick={() => reanalyse(log.id)} disabled={busy}>
                    Re-analyse
                  </Button>
                  <Button size="sm" variant="danger" icon={Trash2} onClick={() => remove(log.id)}
                    aria-label="Delete panel" />
                </div>
              );
            })
          )}
        </Card>
      )}
    </Page>
  );
}
